import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import Login from "./pages/login";
import reportWebVitals from "./reportWebVitals";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  RouteProps,
  useLocation,
} from "react-router-dom";
import Dashboard from "./pages/dashboard";
import NovoVeiculo from "./pages/veiculos/novoVeiculo";
import Veiculos from "./pages/veiculos";
import EditVehiclePage from "./pages/veiculos/editarVeiculo";
import ChecklistPage from "./pages/checklist/checklistPage";
import AddClientPage from "./pages/veiculos/vincularVeiculo";
import Clientes from "./pages/cliente";
import AddClient from "./pages/cliente/novoCliente";
import EditClient from "./pages/cliente/editarCliente";
import ViewClientLinkedPage from "./pages/veiculos/visualizarClienteVinculado";
import LayoutWithNavbar from "./components/layoutWithNavbar";
import AddVehiclePage from "./pages/cliente/vincularCliente";
import ViewVehicleLinkedPage from "./pages/cliente/visualizarVeiculoVinculado";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VisualizarChecklist from "./pages/checklist/visualizarChecklist";
import ScrollToTop from "./scrollToTop";
import VehicleTransfer from "./pages/transferenciaVeiculo";
import ExcluirVeiculo from "./pages/excluirVeiculo";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: process.env.REACT_APP_API_DSN,
  integrations: [Sentry.replayIntegration()],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: process.env.NODE_ENV,
});

window.onerror = function (message, source, lineno, colno, error) {
  Sentry.captureException(error || new Error(message as string));
};

window.addEventListener("unhandledrejection", (event) => {
  Sentry.captureException(event.reason);
});

const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  return !!token;
};

const PrivateRoute: React.FC<RouteProps> = ({ children }) => {
  const location = useLocation();
  const authenticated = isAuthenticated();

  if (!authenticated) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status || 500;

        if (status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("userData");
          window.localStorage.clear();
          window.location.href = "/";
        }

        Sentry.captureException(error);
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return (
    <div>
      {!isOnline && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "red",
            color: "white",
            padding: "10px",
            textAlign: "center",
            zIndex: 1000,
          }}
        >
          Você está offline - Por favor, verifique sua conexão com a internet.
        </div>
      )}

      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <Dashboard />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/veiculos"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <Veiculos />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/novoVeiculo"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <NovoVeiculo />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/editarVeiculo/:vehicleId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <EditVehiclePage />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/checklist/:checklistId/:vehicleId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <ChecklistPage />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/VisualizarChecklist/:id_checklist_respondido"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <VisualizarChecklist />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/cliente"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <Clientes />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/novoCliente"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <AddClient />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/novoCliente/:companyId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <AddClient />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/editarCliente/:clientId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <EditClient />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/novoVeiculo/:clientId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <NovoVeiculo />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/novoVeiculo/:companyId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <NovoVeiculo />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/cliente/:vehicleId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <AddClientPage />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/visualizarClienteVinculado/:vehicleId/:clientId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <ViewClientLinkedPage />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/veiculos/:clientId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <AddVehiclePage />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/visualizarVeiculoVinculado/:clientId"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <ViewVehicleLinkedPage />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/transferenciaVeiculos"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <VehicleTransfer />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route
            path="/excluirVeiculo"
            element={
              <PrivateRoute>
                <LayoutWithNavbar>
                  <ExcluirVeiculo />
                </LayoutWithNavbar>
              </PrivateRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <ToastContainer />
    </div>
  );
};

const rootElement = document.getElementById("root");
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: 20, textAlign: "center" }}>
          <h2>Oops! Algo deu errado.</h2>
          <button onClick={() => window.location.reload()}>
            Recarregar página
          </button>
        </div>
      }
    >
      <App />
    </Sentry.ErrorBoundary>
  );
} else {
  console.error('Elemento com ID "root" não encontrado.');
}

reportWebVitals(Sentry.captureException);
